/**
 * Gantt Data API Hook
 * Provides data fetching for Gantt chart visualization
 */

import { useQuery } from '@tanstack/react-query';
import api from '../api/client';

const API_BASE = '/planning';

// Types for Gantt data (matching backend response)
export interface GanttOperation {
  id: string;
  operation_id: string;
  order_number: string;
  item_code: string;
  item_description: string;
  operation_sequence: number;
  description: string;
  // Resource assignments
  work_center_code: string | null;
  work_center_name: string | null;
  original_work_center_code: string | null;
  employee_name: string | null;
  // Timing
  planned_start: string;
  planned_end: string;
  duration_hours: number;
  // Original ERP timing for comparison
  erp_planned_start: string | null;
  erp_planned_end: string | null;
  // Delta from original
  start_delta_hours: number | null;
  end_delta_hours: number | null;
  // Status
  order_status: string;
  order_priority: number;
  customer: string;
  required_date: string;
  has_conflicts: boolean;
  approval_status: 'pending' | 'approved' | 'rejected';
  work_center_changed: boolean;
}

export interface GanttDependency {
  from: string;
  to: string;
  type: 'finish_to_start' | 'start_to_start' | 'finish_to_finish';
}

export interface GanttResource {
  code: string;
  name: string;
  capacity_hours_per_day: number;
  group: string;
}

export interface GanttMilestone {
  order_number: string;
  required_date: string;
  customer: string;
  priority: number;
}

export interface GanttResponse {
  scenario_id: string;
  scenario_name: string;
  planning_start: string;
  planning_end: string;
  total_operations: number;
  operations: GanttOperation[];
  dependencies: GanttDependency[];
  resources: GanttResource[];
  milestones: GanttMilestone[];
  grouped: Record<string, GanttOperation[]>;
  group_by: 'work_center' | 'order';
}

// Fetch Gantt data for a scenario
async function fetchGanttData(
  scenarioId: string,
  groupBy: 'work_center' | 'order' = 'work_center'
): Promise<GanttResponse> {
  const response = await api.get(`${API_BASE}/scenarios/${scenarioId}/gantt/?group_by=${groupBy}`);
  return response.data;
}

// Hook: Fetch Gantt data
export function useGanttData(
  scenarioId: string | undefined,
  groupBy: 'work_center' | 'order' = 'work_center',
  enabled = true
) {
  return useQuery({
    queryKey: ['gantt-data', scenarioId, groupBy],
    queryFn: () => fetchGanttData(scenarioId!, groupBy),
    enabled: enabled && !!scenarioId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

// Helper: Get operation color based on status and conflicts
export function getOperationColor(op: GanttOperation): {
  bg: string;
  border: string;
  text: string;
} {
  // Conflicts take priority
  if (op.has_conflicts) {
    return { bg: 'bg-red-100', border: 'border-red-500', text: 'text-red-800' };
  }

  // Approval status
  if (op.approval_status === 'rejected') {
    return { bg: 'bg-gray-100', border: 'border-gray-400', text: 'text-gray-600' };
  }
  if (op.approval_status === 'approved') {
    return { bg: 'bg-green-100', border: 'border-green-500', text: 'text-green-800' };
  }

  // Priority-based coloring for pending
  switch (op.order_priority) {
    case 1: // Highest priority
      return { bg: 'bg-orange-100', border: 'border-orange-500', text: 'text-orange-800' };
    case 2:
      return { bg: 'bg-yellow-100', border: 'border-yellow-500', text: 'text-yellow-800' };
    case 3:
      return { bg: 'bg-blue-100', border: 'border-blue-500', text: 'text-blue-800' };
    default:
      return { bg: 'bg-gray-100', border: 'border-gray-400', text: 'text-gray-700' };
  }
}

// Helper: Get time delta display
export function getTimeDeltaDisplay(hours: number | null): {
  text: string;
  color: string;
} {
  if (hours === null || hours === 0) {
    return { text: 'On time', color: 'text-gray-500' };
  }
  if (hours < 0) {
    return { text: `${Math.abs(hours).toFixed(1)}h earlier`, color: 'text-green-600' };
  }
  return { text: `${hours.toFixed(1)}h later`, color: 'text-red-600' };
}

// Helper: Calculate date range for visible timeline
export function calculateDateRange(
  operations: GanttOperation[]
): { start: Date; end: Date } {
  if (operations.length === 0) {
    const now = new Date();
    return {
      start: now,
      end: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000),
    };
  }

  const dates = operations.flatMap((op) => [
    new Date(op.planned_start),
    new Date(op.planned_end),
  ]);

  return {
    start: new Date(Math.min(...dates.map((d) => d.getTime()))),
    end: new Date(Math.max(...dates.map((d) => d.getTime()))),
  };
}

// Helper: Generate date labels for timeline header
export function generateDateLabels(
  start: Date,
  end: Date,
  viewMode: 'day' | 'week' | 'month'
): { date: Date; label: string }[] {
  const labels: { date: Date; label: string }[] = [];
  const current = new Date(start);

  // Normalize to start of day
  current.setHours(0, 0, 0, 0);

  while (current <= end) {
    if (viewMode === 'day') {
      labels.push({
        date: new Date(current),
        label: current.toLocaleDateString('en-GB', {
          weekday: 'short',
          day: '2-digit',
          month: '2-digit'
        }),
      });
      current.setDate(current.getDate() + 1);
    } else if (viewMode === 'week') {
      // Get Monday of the week
      const monday = new Date(current);
      const day = monday.getDay();
      const diff = monday.getDate() - day + (day === 0 ? -6 : 1);
      monday.setDate(diff);

      labels.push({
        date: new Date(monday),
        label: `W${getWeekNumber(monday)}`,
      });
      current.setDate(current.getDate() + 7);
    } else {
      // Month view
      labels.push({
        date: new Date(current),
        label: current.toLocaleDateString('en-GB', {
          month: 'short',
          year: '2-digit'
        }),
      });
      current.setMonth(current.getMonth() + 1);
      current.setDate(1);
    }
  }

  return labels;
}

// Helper: Get ISO week number
function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

// Helper: Calculate operation position on timeline
export function calculateOperationPosition(
  op: GanttOperation,
  timelineStart: Date,
  timelineEnd: Date,
  containerWidth: number
): { left: number; width: number } {
  const totalDuration = timelineEnd.getTime() - timelineStart.getTime();
  const opStart = new Date(op.planned_start).getTime();
  const opEnd = new Date(op.planned_end).getTime();

  const left = ((opStart - timelineStart.getTime()) / totalDuration) * containerWidth;
  const width = Math.max(
    ((opEnd - opStart) / totalDuration) * containerWidth,
    4 // Minimum width for visibility
  );

  return { left, width };
}
